import moment from 'moment';

export interface CaseSortWorkerRequest {
    requestId: number;
    rows: any[];
    sortActive: string;
    sortDirection: 'asc' | 'desc' | '';
    columnKeyMap: Record<string, string>;
    useStrictValidation: boolean;
}

export interface CaseSortWorkerResponse {
    requestId: number;
    sortedIndexes: number[];
}

export const STRICT_DATE_FORMATS = [
    'MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY',
    'YYYY/MM/DD', 'MMMM D, YYYY', 'MMM D, YYYY', 'D MMMM YYYY', 'D MMM YYYY',
    'MM/DD/YY', 'MM-DD-YY', 'YY-MM-DD', 'DD/MM/YY', 'DD-MM-YY',
    'MM/DD/YYYY h:mm A', 'MM-DD-YYYY h:mm A', 'YYYY-MM-DD h:mm A', 'DD/MM/YYYY h:mm A', 'DD-MM-YYYY h:mm A',
    'MM/DD/YYYY HH:mm', 'MM-DD-YYYY HH:mm', 'YYYY-MM-DD HH:mm', 'DD/MM/YYYY HH:mm', 'DD-MM-YYYY HH:mm',
    'MM/DD/YYYY h:mm:ss A', 'MM-DD-YYYY h:mm:ss A', 'YYYY-MM-DD h:mm:ss A',
    'MM/DD/YYYY HH:mm:ss', 'MM-DD-YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss',
    'MM/DD/YY h:mm A', 'MM-DD-YY h:mm A', 'DD/MM/YY h:mm A', 'DD-MM-YY h:mm A',
    moment.ISO_8601
];

export function isDateValueStrict(value: string): boolean {
    return moment(value, STRICT_DATE_FORMATS).isValid();
}

export function isDateValueLoose(value: string): boolean {
    return moment(value).isValid();
}

export function getCaseSortKey(
    row: any,
    columnDefinition: string,
    columnKeyMap: Record<string, string>,
    useStrictValidation: boolean
): string | number {
    const mapKey = columnKeyMap[columnDefinition];
    const value = mapKey ? row[mapKey] : row[columnDefinition];

    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'number') {
        return value;
    }

    if (typeof value === 'string') {
        const isDate = useStrictValidation ? isDateValueStrict(value) : isDateValueLoose(value);
        if (isDate) {
            return new Date(value).getTime();
        }

        return value.toLowerCase();
    }

    return `${value}`.toLowerCase();
}

// Returns the indexes of `rows` in sorted order (rows itself is left untouched),
// so callers can reorder their own array/object references without a deep clone.
export function computeSortedIndexes(
    rows: any[],
    sortActive: string,
    sortDirection: 'asc' | 'desc' | '',
    columnKeyMap: Record<string, string>,
    useStrictValidation: boolean
): number[] {
    const indexes = rows.map((_, index) => index);

    if (!sortActive || !sortDirection) {
        return indexes;
    }

    const direction = sortDirection === 'asc' ? 1 : -1;
    const keyCache = rows.map(row => getCaseSortKey(row, sortActive, columnKeyMap, useStrictValidation));

    indexes.sort((a, b) => {
        const aValue = keyCache[a];
        const bValue = keyCache[b];

        if (aValue == null && bValue == null) {
            return 0;
        }

        if (aValue == null) {
            return -1 * direction;
        }

        if (bValue == null) {
            return 1 * direction;
        }

        let order: number;
        if (aValue < bValue) {
            order = -1;
        } else if (aValue > bValue) {
            order = 1;
        } else {
            order = 0;
        }

        return order * direction;
    });

    return indexes;
}
